// =============================================================================
// VandieZ admin-web -- Ticket detail (/tickets/:ticketId)
// =============================================================================
// Renders the full message thread, an admin reply box, and a status
// transition strip (Open / In progress / Resolved / Closed + Mine
// assign + Priority pick). Replies auto-flip Open → In progress and
// claim the ticket for the calling admin if unassigned.
// =============================================================================

import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  Loader2,
} from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { supportAdminApi } from "@/api/client";
import { PageHeader } from "@/components/AdminLayout";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";
import { ApiError } from "@/types/auth";
import type {
  TicketMessageRead,
  TicketStatus,
  TicketWithMessages,
} from "@/types/support";

const STATUS_PILL: Record<TicketStatus, string> = {
  open: "bg-warning-subtle text-warning-subtle-foreground",
  in_progress: "bg-primary-subtle text-primary-subtle-foreground",
  resolved: "bg-success-subtle text-success-subtle-foreground",
  closed: "bg-muted text-muted-foreground",
};

const STATUS_TRANSITIONS: { label: string; value: TicketStatus }[] = [
  { label: "Open", value: "open" },
  { label: "In progress", value: "in_progress" },
  { label: "Resolved", value: "resolved" },
  { label: "Closed", value: "closed" },
];

function formatDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

function MessageBubble({ msg }: { msg: TicketMessageRead }): React.JSX.Element {
  const isAdmin = msg.sender_role === "admin";
  return (
    <div className={cn("flex", isAdmin ? "justify-start" : "justify-end")}>
      <div
        className={cn(
          "max-w-[480px] rounded-lg px-3 py-2",
          isAdmin ? "bg-muted" : "bg-primary/10",
        )}
      >
        <div className="text-xs text-muted-foreground">
          {isAdmin ? "VandieZ support" : msg.sender_role} ·{" "}
          {formatDate(msg.created_at)}
        </div>
        <div className="mt-1 whitespace-pre-wrap text-sm">{msg.body}</div>
      </div>
    </div>
  );
}

export function TicketDetailPage(): React.JSX.Element {
  const { ticketId } = useParams<{ ticketId: string }>();
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const [ticket, setTicket] = useState<TicketWithMessages | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [replyBody, setReplyBody] = useState<string>("");
  const [sending, setSending] = useState<boolean>(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const load = useCallback(
    async (signal?: AbortSignal) => {
      if (accessToken === null || ticketId === undefined) {
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const res = await supportAdminApi.detail(
          accessToken,
          ticketId,
          signal,
        );
        setTicket(res);
      } catch (e: unknown) {
        if (e instanceof Error && e.name === "AbortError") {
          return;
        }
        const msg =
          e instanceof ApiError ? e.problem.detail : "Unexpected error.";
        setError(msg);
      } finally {
        setLoading(false);
      }
    },
    [accessToken, ticketId],
  );

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => {
      controller.abort();
    };
  }, [load]);

  const sendReply = async () => {
    if (
      accessToken === null ||
      ticketId === undefined ||
      replyBody.trim().length === 0
    ) {
      return;
    }
    setSending(true);
    setSendError(null);
    try {
      await supportAdminApi.reply(accessToken, ticketId, {
        body: replyBody.trim(),
      });
      setReplyBody("");
      await load();
    } catch (e: unknown) {
      const msg =
        e instanceof ApiError ? e.problem.detail : "Unexpected error.";
      setSendError(msg);
    } finally {
      setSending(false);
    }
  };

  const transitionStatus = async (newStatus: TicketStatus) => {
    if (accessToken === null || ticketId === undefined) {
      return;
    }
    try {
      await supportAdminApi.updateStatus(accessToken, ticketId, {
        status: newStatus,
        assign_to_self: true,
      });
      setFlash(`Ticket marked ${newStatus.replace(/_/g, " ")}.`);
      await load();
    } catch (e: unknown) {
      const msg =
        e instanceof ApiError ? e.problem.detail : "Unexpected error.";
      setSendError(msg);
    }
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Ticket"
        description="Reply, transition status, and assign to yourself in one place."
        actions={
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { navigate("/tickets"); }}
          >
            <ArrowLeft className="mr-1 h-4 w-4" /> Queue
          </Button>
        }
      />
        {error !== null && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        {flash !== null && (
          <Alert>
            <CheckCircle2 className="h-4 w-4" />
            <AlertTitle>Saved</AlertTitle>
            <AlertDescription>{flash}</AlertDescription>
          </Alert>
        )}

        {loading && ticket === null ? (
          <div className="flex items-center gap-2 py-8 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : ticket !== null ? (
          <>
            <Card>
              <CardHeader>
                <div className="flex items-baseline justify-between gap-4">
                  <div>
                    <CardTitle>{ticket.subject}</CardTitle>
                    <CardDescription>
                      <span className="capitalize">{ticket.category}</span>{" "}
                      · raised by{" "}
                      <span className="font-medium">
                        {ticket.user_name ?? `${ticket.user_id.slice(0, 8)}…`}
                      </span>
                      {ticket.user_phone !== null && (
                        <span className="font-mono text-xs ml-1">
                          ({ticket.user_phone})
                        </span>
                      )}{" "}
                      · {formatDate(ticket.created_at)}
                    </CardDescription>
                  </div>
                  <span
                    className={cn(
                      "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
                      STATUS_PILL[ticket.status],
                    )}
                  >
                    {ticket.status.replace(/_/g, " ")}
                  </span>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-1">
                  {STATUS_TRANSITIONS.map((t) => (
                    <Button
                      key={t.value}
                      size="sm"
                      variant={ticket.status === t.value ? "default" : "outline"}
                      onClick={() => void transitionStatus(t.value)}
                      disabled={ticket.status === t.value}
                    >
                      {t.label}
                    </Button>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Thread</CardTitle>
                <CardDescription>
                  Newest at the bottom. Admin messages render left;
                  user messages right.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {ticket.messages.map((m) => (
                  <MessageBubble key={m.id} msg={m} />
                ))}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Reply</CardTitle>
                <CardDescription>
                  Posts as the calling admin; auto-flips Open → In progress
                  and assigns the ticket to you if unassigned.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                <Textarea
                  value={replyBody}
                  onChange={(e) => {
                    setReplyBody(e.target.value);
                  }}
                  maxLength={4000}
                  placeholder="Type your reply…"
                  disabled={sending}
                />
                {sendError !== null && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{sendError}</AlertDescription>
                  </Alert>
                )}
                <div className="flex justify-end">
                  <Button
                    onClick={() => void sendReply()}
                    disabled={sending || replyBody.trim().length === 0}
                  >
                    {sending ? (
                      <>
                        <Loader2 className="mr-1 h-4 w-4 animate-spin" />{" "}
                        Sending…
                      </>
                    ) : (
                      "Send reply"
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </>
        ) : null}
    </div>
  );
}
